import React, { useEffect, useState } from 'react';
import {
    View,
    TouchableOpacity,
    Image,
} from 'react-native';

import { translate } from '../../services/translate';
import MyAppText from '../../components/MyAppText';
import getBestContrastColor from '../../services/best-contrast-color';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import SmartSearchEventDetailModal from './SmartSearchEventDetailModal';
import Badge from '../../components/Badge';
import { TextMask } from 'react-native-masked-text';
import moment from 'moment';
import MyDataTable from '../../components/MyDataTable';


interface EventListProps {
    events: FacialDetection[];
    facialSent: string;
    noResultsText?: string;
    isLoading: boolean;
}

export default function SmartSearchEventList({ events, noResultsText, facialSent, isLoading }: EventListProps) {
    const { styles } = useStyles(stylesheet);

    const [selectedEvent, setSelectedEvent] = useState<FacialDetection>();
    const [isModalVisible, setIsModalVisible] = useState<boolean>(false);

    function handleFormClose() {
        setSelectedEvent(undefined);
        setIsModalVisible(false);
    }

    useEffect(() => {
        for (const event of events) {
            if (event.person) {
                event.person.facePictures = event.person.facePictures.filter(fp => {
                    const eventCreatedAt = moment(event.createdAt);
                    return moment(fp.createdAt).isBefore(eventCreatedAt) &&
                        (fp.deletedAt === null || moment(fp.deletedAt).isAfter(eventCreatedAt));
                });
            }
        }
    }, [events]);

    return <>
        <MyDataTable
            progressPending={isLoading}
            data={events}
            noResultsText={noResultsText}
            columns={[{
                name: translate('tableColumnRegisteredCapture'),
                width: '166',
                center: true,
                cell: (row) => <View style={{ paddingVertical: 8 }}>
                    <Image
                        style={{
                            height: 96,
                            width: 72,
                            borderRadius: 4,
                            overflow: 'hidden',
                        }}
                        source={{ uri: row.imageUrl }}
                    />
                </View>,
            }, {
                name: translate('tableColumnRegisteredReference'),
                width: '166',
                center: true,
                cell: (row) => row.person?.facePictures.length ?
                    <Image
                        style={{
                            height: 96,
                            width: 72,
                            borderRadius: 4,
                            overflow: 'hidden',
                        }}
                        source={{ uri: row.person.facePictures[0].imageUrl }}
                    />
                    : <MyAppText style={styles.noMatch} >{translate('noMatch')}</MyAppText>
                ,
            }, {
                name: translate('tableColumnCaptureAgainstFacialSent'),
                width: '300',
                center: true,
                selector: (row) => row.confidence ? `${(Math.floor(Number(row.confidence) * 100)).toFixed(0)}%` : '',
            }, {
                name: translate('tableColumnIdentification'),
                grow: 3,
                center: true,
                cell: (row) => row.person ? <View style={{ flexDirection: 'column', rowGap: 4, flexWrap: 'wrap', alignContent: 'center', justifyContent: 'center', }}>
                    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 4 }}>
                        {row.person.markers.map((marker) =>
                            <Badge
                                mode='ROUNDED'
                                key={JSON.stringify(marker)}
                                style={{ alignSelf: 'center' }}
                                backgroundColor={marker.color}
                                textColor={getBestContrastColor(marker.color)}
                            >
                                {marker.name}
                            </Badge>
                        )}
                    </View>
                    <TextMask
                        style={styles.cpfText}
                        value={row.person.document ?? ''}
                        type='cpf'
                    />
                </View> : null,
            }, {
                name: translate('tableColumnInformation'),
                width: '105',
                center: true,
                cell: (row) => <TouchableOpacity onPress={() => {
                    setSelectedEvent(row);
                    setIsModalVisible(true);
                }}>
                    <MyAppText style={styles.seeMore}>+ {translate('seeMore')}</MyAppText>
                </TouchableOpacity>,
            }]}

        />
        <SmartSearchEventDetailModal
            onClose={handleFormClose}
            eventId={selectedEvent?.id}
            facialSent={facialSent}
            facialSentConfidence={selectedEvent?.confidence}
            isModalVisible={isModalVisible}
        />
    </>;
}

const stylesheet = createStyleSheet(theme => ({
    noMatch: {
        fontSize: 14,
        fontWeight: '400',
        textAlign: 'center',
        color: theme.colors.neutral['400']
    },
    seeMore: {
        fontSize: 14,
        fontWeight: '600',
        color: theme.colors.secondaryButton.text
    },
    cpfText: {
        fontSize: 14,
        fontWeight: '400',
        color: theme.colors.neutral['400']
    }
}));
